import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import { getLocalJWT, getLocalUserId } from "../../../components/functions/utilities";
import { socketInit } from "../../../components/Hooks/socketInit";
import {
  PULSE_DEADLINE_SUBMITTED_SOCKET_EVENT,
  PULSE_EMPTY_TAB_DATA,
  PULSE_COUNTED_TAB_CATEGORIES,
  PULSE_OVERVIEW_ERROR_MESSAGE,
} from "../../../constants/pulse.constants";
import {
  PulseContextType,
  PulseOverviewData,
  PulseProviderProps,
  PulseTabStatusMap,
  PulseTabKey,
} from "../../../interfaces/pulse.interfaces";
import { PulseRestS } from "../../../rest/pulse-rest-service";

export const PulseContext = createContext<PulseContextType>(null);

// react-final-form convention for any tab with a real form/grid (e.g. the reused
// DebtScheduleGrid/ShareholderGrid, or a new Bank Statement upload form):
//   - initialValues comes from usePulseContext().tabData[tabKey] — never re-fetched
//     just to seed a form, since PulseContext already holds it.
//   - onSubmit calls that tab's own REST endpoint, then on success calls
//     updateTabData(tabKey, (prev) => ...) to write the result back into the cache
//     (and dispatches the sidebar-badge Redux update where relevant, once that
//     slice exists).
//   - A Field/FieldArray never calls updateTabData directly — only onSubmit does,
//     same as Underwriting: react-final-form owns in-progress edits, the cache
//     only receives the committed result.
export const usePulseContext = (): PulseContextType => {
  const context = useContext(PulseContext);

  if (context === null) {
    throw new Error("usePulseContext must be used within a PulseProvider");
  }

  return context;
};

// Stub — replaced with a real per-tab REST call (RestS/pulse-rest-service) as each tab is built.
const fetchTabDataFromApi = async (tabKey: PulseTabKey): Promise<any> => {
  // TODO: wire real calls per tab (connections, gst, bankStatements, books, financials, debtInvestments)
  return null;
};

const PulseProvider = ({ children }: PulseProviderProps) => {
  const navigate = useNavigate();
  // null while checking; Pulse renders and loads only once access is confirmed.
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [summary, setSummary] = useState<PulseOverviewData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [tabData, setTabData] = useState<Record<PulseTabKey, any | null>>(
    PULSE_EMPTY_TAB_DATA
  );
  const isMountedRef = useRef(true);

  const loadSummary = useCallback(
    () =>
      PulseRestS.getOverview()
        .then((data) => {
          if (!isMountedRef.current) return;
          setSummary(data);
          setSummaryError(null);
        })
        .catch((error) => {
          if (!isMountedRef.current) return;
          const message =
            error instanceof Error
              ? error.message
              : PULSE_OVERVIEW_ERROR_MESSAGE;
          setSummary(null);
          setSummaryError(message);
          toast.error(message);
        })
        .finally(() => {
          if (isMountedRef.current) setSummaryLoading(false);
        }),
    []
  );

  useEffect(() => {
    isMountedRef.current = true;
    const isMounted = () => isMountedRef.current;

    // Same guard as UnderwritingHome: no access → 404, replacing history.
    PulseRestS.checkAccess().then((allowed) => {
      if (!isMounted()) return;
      if (!allowed) {
        navigate("/404", { replace: true });
        return;
      }
      setHasAccess(true);
      loadSummary();
    });

    // Runs once on mount — this Provider is mounted at the /pulse/* route boundary
    // (App.tsx), so it survives tab switching and only remounts if the user leaves
    // /pulse entirely, same lifecycle as UnderWritingFormProvider.

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Lives here rather than in a tab so a deadline closed while the user is on
  // another tab still refreshes every tab badge from the server's counts.
  useEffect(() => {
    if (!hasAccess) return;
    const userId = getLocalUserId();
    const token = getLocalJWT();
    if (!userId || !token) return;

    const socket = socketInit(userId.toString(), token);
    let hasDisconnected = false;
    const joinUserRoom = () =>
      socket.emit("JOIN_USER_ROOM", { roomId: `user_${userId}` });
    const handleConnect = () => {
      joinUserRoom();
      // Events may have been missed while offline.
      if (hasDisconnected) void loadSummary();
    };
    const handleDisconnect = () => {
      hasDisconnected = true;
    };
    const handleDeadlineSubmitted = () => void loadSummary();

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on(PULSE_DEADLINE_SUBMITTED_SOCKET_EVENT, handleDeadlineSubmitted);
    if (socket.connected) joinUserRoom();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off(PULSE_DEADLINE_SUBMITTED_SOCKET_EVENT, handleDeadlineSubmitted);
      socket.disconnect();
    };
  }, [hasAccess, loadSummary]);

  // Count plus which overview bucket the category sits in, so the tab badge
  // can colour itself the way the overview cards do.
  const tabStatus = useMemo<PulseTabStatusMap | null>(() => {
    if (!summary) return null;

    return PULSE_COUNTED_TAB_CATEGORIES.reduce((statuses, category) => {
      const bucket = summary.needActions?.[category]
        ? "needActions"
        : summary.dueToday?.[category]
          ? "dueToday"
          : "allSet";
      statuses[category] = {
        bucket,
        pendingActions: summary[bucket]?.[category]?.pendingActions ?? 0,
      };
      return statuses;
    }, {} as PulseTabStatusMap);
  }, [summary]);

  const fetchTabData = async (tabKey: PulseTabKey) => {
    if (tabData[tabKey] !== null) {
      // already cached — no-op, this is what avoids refetching on tab revisit
      return;
    }

    const data = await fetchTabDataFromApi(tabKey);
    setTabData((prev) => ({ ...prev, [tabKey]: data }));
  };

  const updateTabData = (tabKey: PulseTabKey, updater: (prev: any) => any) => {
    setTabData((prev) => ({ ...prev, [tabKey]: updater(prev[tabKey]) }));
  };

  const value: PulseContextType = {
    summary,
    summaryLoading,
    summaryError,
    tabStatus,
    tabData,
    fetchTabData,
    updateTabData,
  };

  if (!hasAccess) return <CircularLoader />;

  return <PulseContext.Provider value={value}>{children}</PulseContext.Provider>;
};

export default PulseProvider;
